import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Play, Trash2, Clock, CheckCircle2, XCircle, Loader, ChevronDown, ChevronUp,
  Pause, PlayCircle, Sparkles, ShieldAlert, Pencil, Archive, Globe2, RefreshCw,
  ArrowRight, Bot, FileCheck2, Send, HelpCircle, Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import {
  AUTOMATIONS_MODES as MODES,
  GENERATION_FREQUENCIES,
  GENERATION_SCHEDULE_DISCLAIMER,
  PUBLISHING_MODE_HINT,
  RUN_ONCE_CREDIT_WARNING,
  SUPPORTED_TASK_ACTIONS,
  WEEKDAY_OPTIONS,
  formatPlaybookLocal,
  hasGenerationSchedule,
  identityLabel,
  modeLabel,
  playbookFormFromRecord,
  playbookPayloadFromForm,
  resolveAutopilotPublishSummary,
  scheduleFormFromPlaybook,
  schedulePayloadFromForm,
} from './automationsControls';

const FREQUENCIES = ['manual', 'hourly', 'daily'];
const ACTIONS = SUPPORTED_TASK_ACTIONS;

function nextPlaybookAction(playbook) {
  if (playbook.paused) {
    return { tone: 'amber', title: 'Paused', detail: 'Resume this campaign before creating or publishing anything.' };
  }
  if (playbook.source_app_id && playbook.last_source_status !== 'succeeded') {
    return { tone: 'amber', title: 'Next: inspect the website', detail: 'Check the approved pages before asking AI to create a draft.' };
  }
  if ((playbook.awaiting_approval_count || 0) > 0) {
    return { tone: 'primary', title: `Next: review ${playbook.awaiting_approval_count} draft${playbook.awaiting_approval_count === 1 ? '' : 's'}`, detail: 'Approve, edit, or reject them in the approval inbox below.' };
  }
  if (!hasGenerationSchedule(playbook)) {
    return { tone: 'primary', title: 'Ready to create drafts', detail: 'Create one draft now or schedule recurring AI drafts.' };
  }
  if (!playbook.generation_schedule_enabled) {
    return { tone: 'amber', title: 'Draft schedule disabled', detail: 'Edit the schedule and enable recurring AI generation when ready.' };
  }
  if (playbook.next_generation_at) {
    const value = String(playbook.next_generation_at);
    return {
      tone: 'green',
      title: 'Working automatically',
      detail: `Next AI draft: ${formatPlaybookLocal(value.endsWith('Z') ? value : `${value}Z`, playbook.timezone || 'UTC')}`,
    };
  }
  return { tone: 'green', title: 'Automation active', detail: 'Mayr Ops will calculate the next draft time.' };
}

function WorkflowOverview({ playbooks, inbox, pausePublishing, showHelp, onToggleHelp }) {
  const activeCampaigns = playbooks.filter((playbook) => !playbook.paused).length;
  const scheduledCampaigns = playbooks.filter(
    (playbook) => !playbook.paused && playbook.generation_schedule_enabled && playbook.next_generation_at,
  );
  const nextScheduled = scheduledCampaigns
    .map((playbook) => {
      const value = String(playbook.next_generation_at);
      const normalized = value.endsWith('Z') ? value : `${value}Z`;
      return { playbook, normalized, time: new Date(normalized).getTime() };
    })
    .filter((item) => !Number.isNaN(item.time))
    .sort((a, b) => a.time - b.time)[0];
  const nextAction = pausePublishing
    ? 'Everything is paused: no AI drafts and no LinkedIn publishing.'
    : nextScheduled
      ? `Next AI draft: ${nextScheduled.playbook.name} — ${formatPlaybookLocal(nextScheduled.normalized, nextScheduled.playbook.timezone || 'UTC')}`
      : inbox.length > 0
        ? `${inbox.length} draft${inbox.length === 1 ? '' : 's'} waiting for your review.`
        : 'No recurring AI draft is scheduled. Create one now or schedule a campaign.';

  const steps = [
    { icon: Globe2, title: '1. Choose the product', text: 'Connect an App Registry website or use a manual campaign.' },
    { icon: Bot, title: '2. Create AI drafts', text: 'Create one now or schedule recurring drafts. Nothing is public yet.' },
    { icon: FileCheck2, title: '3. Review and approve', text: 'Check the content, links, pictures, and quality results.' },
    { icon: Send, title: '4. Publish or schedule', text: 'Use Draft Posts to publish now or choose an exact LinkedIn time.' },
  ];

  return (
    <section className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4" data-testid="automation-workflow-guide">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-foreground">How Mayr Ops automation works</h2>
          <p className="text-xs text-muted-foreground mt-1">AI draft creation and public LinkedIn publishing are separate steps, so you always know what happens next.</p>
        </div>
        <button type="button" onClick={onToggleHelp} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/20 text-primary text-xs" data-testid="automation-help-toggle">
          <HelpCircle className="w-3.5 h-3.5" /> {showHelp ? 'Hide detailed help' : 'Show detailed help'}
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-4" data-testid="automation-workflow-steps">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="rounded-lg border border-white/10 bg-background/30 p-3 relative">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-primary" />
                <p className="text-xs font-semibold text-foreground">{step.title}</p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">{step.text}</p>
              {index < steps.length - 1 && <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50 z-10" />}
            </div>
          );
        })}
      </div>

      <div className="grid gap-2 sm:grid-cols-3" data-testid="automation-status-summary">
        <div className="rounded-lg bg-background/30 p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Master status</p>
          <p className={`text-sm font-medium mt-1 ${pausePublishing ? 'text-amber-200' : 'text-status-up'}`}>{pausePublishing ? 'All automation paused' : 'Automation available'}</p>
        </div>
        <div className="rounded-lg bg-background/30 p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Right now</p>
          <p className="text-sm font-medium text-foreground mt-1">{activeCampaigns} active campaign{activeCampaigns === 1 ? '' : 's'} · {inbox.length} to review</p>
        </div>
        <div className="rounded-lg bg-background/30 p-3 sm:col-span-1" data-testid="automation-next-action">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">What happens next</p>
          <p className="text-xs text-foreground mt-1">{nextAction}</p>
        </div>
      </div>

      {showHelp && (
        <div className="rounded-lg border border-white/10 bg-background/40 p-4" data-testid="automation-detailed-help">
          <h3 className="text-sm font-semibold text-foreground">What every control does</h3>
          <div className="grid gap-3 mt-3 md:grid-cols-2 text-xs">
            <div><span className="font-medium text-foreground">Inspect website:</span> <span className="text-muted-foreground">Reads only approved public pages, checks Search readiness, uses no AI credits, and publishes nothing.</span></div>
            <div><span className="font-medium text-foreground">Create draft now:</span> <span className="text-muted-foreground">Uses one AI run to create one draft immediately. It does not change any recurring schedule.</span></div>
            <div><span className="font-medium text-foreground">Schedule AI drafts:</span> <span className="text-muted-foreground">Controls when Mayr Ops creates drafts. It does not schedule LinkedIn publishing.</span></div>
            <div><span className="font-medium text-foreground">Approval inbox:</span> <span className="text-muted-foreground">Shows what needs your decision. Edit, approve, or reject before publishing.</span></div>
            <div><span className="font-medium text-foreground">Publishing mode:</span> <span className="text-muted-foreground">Draft only stores drafts; Approval required waits for you; Guarded autopilot may publish only after every safety check passes.</span></div>
            <div><span className="font-medium text-foreground">Pause controls:</span> <span className="text-muted-foreground">Campaign Pause stops that campaign. Pause all automation stops both AI generation and LinkedIn publishing everywhere.</span></div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Google Search readiness is guidance about page setup; it does not guarantee ranking. Google Ads is not activated from this page.</p>
        </div>
      )}
    </section>
  );
}

function CreateTaskModal({ apps, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', app_id: '', action_name: 'health_check', frequency: 'manual', notification_behavior: 'onscreen',
  });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tasks/', { ...form, app_id: form.app_id || null });
      toast.success('Task created');
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-task-modal">
      <div className="bg-card border border-white/10 rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-lg font-semibold text-foreground">New App Check Task</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="task-name-input" placeholder="Task name" />
          <select value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="">No app</option>
            {apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={form.action_name} onChange={(e) => setForm({ ...form, action_name: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="task-action-select">
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm">
            {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50" data-testid="create-task-submit">{loading ? 'Creating...' : 'Create Task'}</button>
        </form>
      </div>
    </div>
  );
}

function PlaybookFormModal({ identities, apps, initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => playbookFormFromRecord(initial || {}, identities));
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = playbookPayloadFromForm(form);
      const res = editing
        ? await api.put(`/campaign-automations/playbooks/${initial.id}`, payload)
        : await api.post('/campaign-automations/playbooks', payload);
      toast.success(editing ? 'Playbook updated' : 'Campaign playbook created');
      onSaved(res.data, editing);
      onClose();
    } catch (err) {
      toast.error(
        err.code === 'AUTOPILOT_WINDOW_INVALID'
          ? err.message
          : (err.response?.data?.detail || 'Failed to save playbook')
      );
    } finally {
      setLoading(false);
    }
  };
  const field = (key, props = {}) => (
    <input
      {...props}
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })}
      className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
    />
  );
  const area = (key, placeholder) => (
    <textarea
      placeholder={placeholder}
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[50px]"
    />
  );
  const labelledField = (key, label, help, props = {}) => (
    <label className="block space-y-1">
      <span className="block text-xs font-medium text-foreground">{label}</span>
      {help && <span className="block text-[11px] text-muted-foreground">{help}</span>}
      {field(key, props)}
    </label>
  );
  const labelledArea = (key, label, help, placeholder = '') => (
    <label className="block space-y-1">
      <span className="block text-xs font-medium text-foreground">{label}</span>
      {help && <span className="block text-[11px] text-muted-foreground">{help}</span>}
      {area(key, placeholder)}
    </label>
  );
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="playbook-form-modal">
      <div className="bg-card border border-white/10 rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-card">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{editing ? 'Edit campaign' : 'Create campaign'}</h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">All controls are kept here, grouped by what they affect.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <section className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="playbook-product-section">
            <div>
              <p className="text-sm font-semibold text-foreground">1. What are we promoting?</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Name the campaign, choose the product, and optionally connect its public website.</p>
            </div>
            <label className="block space-y-1">
              <span className="block text-xs font-medium text-foreground">Campaign name</span>
              <input required placeholder="Example: Smart QR launch" data-testid="playbook-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </label>
            {labelledField('product_service', 'Product or service', 'The exact offer the posts should focus on.', { placeholder: 'Example: Smart QR digital business cards' })}
            <div className="rounded-lg border border-white/10 bg-background/20 p-3 space-y-2" data-testid="content-source-section">
              <div>
                <p className="text-xs font-medium text-foreground">App or website content source</p>
                <p className="text-[11px] text-muted-foreground">Mayr Ops inspects only approved public pages before creating drafts. Inspection uses no AI credits and publishes nothing.</p>
              </div>
              <select
                value={form.source_app_id}
                onChange={(e) => {
                  const app = (apps || []).find((item) => item.id === e.target.value);
                  setForm({
                    ...form,
                    source_app_id: e.target.value,
                    source_urls: e.target.value ? (form.source_urls || app?.base_url || '') : '',
                  });
                }}
                className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="playbook-source-app-select"
              >
                <option value="">Manual campaign (no website inspection)</option>
                {(apps || []).filter((app) => app.enabled !== false).map((app) => (
                  <option key={app.id} value={app.id}>{app.name} — {app.base_url}</option>
                ))}
              </select>
              {form.source_app_id && labelledArea('source_urls', 'Approved pages', 'Same website only, one page per line, maximum 5.', 'https://example.com/product')}
            </div>
            {labelledArea('business_objective', 'Business goal', 'What should this campaign achieve?', 'Example: Generate qualified demo requests')}
            {labelledArea('target_audience', 'Target audience', 'Who should recognize this as relevant?', 'Example: Local business owners with multiple locations')}
          </section>

          <section className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="playbook-content-rules-section">
            <div>
              <p className="text-sm font-semibold text-foreground">2. What may AI say?</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">These rules control quality, accuracy, wording, links, and calls to action.</p>
            </div>
            {labelledArea('content_pillars', 'Content themes', 'Topics AI may rotate between. Use commas or one per line.', 'Education, customer results, product tips')}
            {labelledArea('approved_facts', 'Approved facts', 'Claims AI is allowed to make. Use only facts you can support.', 'One fact per line')}
            {labelledArea('prohibited_claims', 'Claims to avoid', 'Promises, wording, or subjects AI must never use.', 'One restriction per line')}
            {labelledArea('tone_rules', 'Writing style', 'Describe the voice, length, language, and formatting.', 'Professional, helpful, plain English, no hype')}
            {labelledField('approved_cta', 'Call to action', 'The approved closing sentence for generated posts.', { placeholder: 'Example: Learn more on our website.' })}
            {labelledArea('allowlisted_urls', 'Approved links', 'Only these public URLs may appear in generated posts. Use commas or one per line.', 'https://mayr-technologies.com')}
          </section>

          <section className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="playbook-approval-section">
            <div>
              <p className="text-sm font-semibold text-foreground">3. What happens after a draft?</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Choose the review level and the exact LinkedIn identity. Publishing remains a separate action.</p>
            </div>
            <div className="space-y-1" data-testid="playbook-publishing-mode-section">
              <label className="block text-xs font-medium text-foreground">Publishing mode</label>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="playbook-mode-select">
                {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground" data-testid="publishing-mode-hint">{PUBLISHING_MODE_HINT}</p>
            </div>
            <label className="block space-y-1">
              <span className="block text-xs font-medium text-foreground">LinkedIn identity</span>
              <span className="block text-[11px] text-muted-foreground">The personal profile or company page that may publish this campaign.</span>
              <select value={form.publishing_identity_id} onChange={(e) => setForm({ ...form, publishing_identity_id: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="playbook-identity-select" required>
                <option value="">Select LinkedIn identity</option>
                {(identities || []).map((identity) => (
                  <option key={identity.id} value={identity.id}>{identity.name} ({identity.type})</option>
                ))}
              </select>
            </label>
            <label className="flex items-start gap-2 text-sm text-muted-foreground rounded-lg bg-secondary/20 p-3">
              <input type="checkbox" checked={form.picture_required} onChange={(e) => setForm({ ...form, picture_required: e.target.checked })} className="mt-0.5" />
              <span><span className="block text-foreground">Require a picture before publishing</span><span className="block text-[11px] mt-0.5">A generated draft cannot enter the publish path until an image is attached.</span></span>
            </label>
          </section>

          <section className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="playbook-limits-section">
            <div>
              <p className="text-sm font-semibold text-foreground">4. Limits and timing</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Safety limits stay visible and editable. The recurring draft schedule is configured after the campaign is saved.</p>
            </div>
            {labelledField('timezone', 'Campaign timezone', 'Used for daily limits, draft schedules, and optional autopilot times.', { placeholder: 'IANA timezone (e.g. Europe/Berlin)', 'data-testid': 'playbook-timezone-input' })}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-foreground mb-1">Minimum quality score</label>
                {field('quality_threshold', { type: 'number', min: 0, max: 100, 'data-testid': 'playbook-quality-threshold' })}
              </div>
              <div>
                <label className="block text-xs text-foreground mb-1">AI drafts per day</label>
                {field('max_generations_per_day', { type: 'number', min: 1, max: 50, 'data-testid': 'playbook-generation-cap' })}
              </div>
              <div>
                <label className="block text-xs text-foreground mb-1">LinkedIn posts per day</label>
                {field('max_publishes_per_day', { type: 'number', min: 1, max: 50, 'data-testid': 'playbook-publishing-cap' })}
              </div>
            </div>
            {form.mode === 'guarded_autopilot' && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2" data-testid="autopilot-publish-window">
                <p className="text-xs font-medium text-foreground">Guarded autopilot LinkedIn time</p>
                <p className="text-[11px] text-muted-foreground">Separate from AI draft creation. Times use {form.timezone || 'UTC'}, and all gates, caps, identity, and pause checks must still pass.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[10px] text-muted-foreground mb-1">Preferred time</label>
                    <input type="time" value={form.autopilot_publish_local_time} onChange={(e) => setForm({ ...form, autopilot_publish_local_time: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="autopilot-publish-time" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground mb-1">Window start</label>
                    <input type="time" value={form.autopilot_publish_window_start} onChange={(e) => setForm({ ...form, autopilot_publish_window_start: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="autopilot-window-start" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground mb-1">Window end</label>
                    <input type="time" value={form.autopilot_publish_window_end} onChange={(e) => setForm({ ...form, autopilot_publish_window_end: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="autopilot-window-end" />
                  </div>
                </div>
              </div>
            )}
          </section>
          <p className="text-[11px] text-muted-foreground" data-testid="playbook-credit-note">LinkedIn only. New campaigns start paused. Pause/Resume stops both AI draft creation and LinkedIn publishing for this campaign. Create draft now may call OpenAI and spend AI credits when configured.</p>
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50" data-testid="playbook-form-submit">
            {loading ? 'Saving...' : (editing ? 'Save campaign' : 'Create campaign')}
          </button>
        </form>
      </div>
    </div>
  );
}

function RunOnceDialog({ playbook, onClose, onConfirm }) {
  const [topic, setTopic] = useState('');
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="run-once-dialog">
      <div className="bg-card border border-white/10 rounded-xl w-full max-w-md shadow-2xl p-5 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Create one AI draft — {playbook.name}</h2>
        <p className="text-xs text-amber-200/90" data-testid="run-once-credit-warning">{RUN_ONCE_CREDIT_WARNING}</p>
        <p className="text-[11px] text-muted-foreground" data-testid="run-once-schedule-note">This creates one draft only. It does not publish to LinkedIn or change the recurring AI draft schedule.</p>
        <textarea
          data-testid="run-once-topic-input"
          placeholder="Optional topic, angle, or content pillar"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[70px]"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-white/10 text-xs" data-testid="run-once-cancel">Cancel</button>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(RUN_ONCE_CREDIT_WARNING)) return;
              onConfirm(topic.trim() || null);
            }}
            className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs"
            data-testid="run-once-confirm"
          >
            Create draft
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleGenerationModal({ playbook, onClose, onSaved }) {
  const editing = hasGenerationSchedule(playbook);
  const [form, setForm] = useState(() => scheduleFormFromPlaybook(playbook));
  const [loading, setLoading] = useState(false);
  const tz = playbook.timezone || 'UTC';

  const toggleWeekday = (day) => {
    const set = new Set(form.weekdays || []);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    setForm({ ...form, weekdays: Array.from(set).sort((a, b) => a - b) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put(
        `/campaign-automations/playbooks/${playbook.id}/generation-schedule`,
        schedulePayloadFromForm(form),
      );
      toast.success(editing ? 'Generation schedule updated' : 'Generation scheduled');
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save generation schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="schedule-generation-modal">
      <div className="bg-card border border-white/10 rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-card">
          <h2 className="text-lg font-semibold text-foreground">{editing ? 'Edit AI draft schedule' : 'Schedule AI drafts'} — {playbook.name}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-1" data-testid="generation-schedule-disclaimer">
            <p className="text-xs font-medium text-amber-100">When AI should create new drafts</p>
            <p className="text-[11px] text-muted-foreground">{GENERATION_SCHEDULE_DISCLAIMER}</p>
            <p className="text-[11px] text-muted-foreground">This is not LinkedIn publishing. Publish Now / Schedule post remain on Draft Posts after review.</p>
            <p className="text-[11px] text-muted-foreground">Times below are in playbook timezone <span className="font-mono text-foreground">{tz}</span>.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={!!form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              data-testid="schedule-enabled-toggle"
            />
            Enable recurring AI generation
          </label>

          <div>
            <label className="block text-xs text-muted-foreground mb-1">Frequency</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
              data-testid="schedule-frequency-select"
            >
              {GENERATION_FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>

          {form.frequency !== 'interval' && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Local time ({tz})</label>
              <input
                type="time"
                required
                value={form.local_time}
                onChange={(e) => setForm({ ...form, local_time: e.target.value })}
                className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="schedule-local-time"
              />
            </div>
          )}

          {form.frequency === 'weekdays' && (
            <div data-testid="schedule-weekdays">
              <label className="block text-xs text-muted-foreground mb-1.5">Weekdays</label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_OPTIONS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleWeekday(d.value)}
                    className={`px-2.5 py-1 rounded-md text-xs border ${(form.weekdays || []).includes(d.value) ? 'bg-primary/20 border-primary/40 text-primary' : 'border-white/10 text-muted-foreground'}`}
                    data-testid={`schedule-weekday-${d.value}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {form.frequency === 'interval' && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Interval (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                required
                value={form.interval_hours}
                onChange={(e) => setForm({ ...form, interval_hours: e.target.value })}
                className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="schedule-interval-hours"
              />
            </div>
          )}

          <p className="text-[11px] text-muted-foreground" data-testid="no-cron-note">No cron expressions — pick daily, weekdays, or an hour interval.</p>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50" data-testid="schedule-generation-save-btn">
              {loading ? 'Saving...' : (editing ? 'Save AI draft schedule' : 'Schedule AI drafts')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlaybookSchedulePanel({ playbook, onSchedule, onCancelSchedule }) {
  const configured = hasGenerationSchedule(playbook);
  const tz = playbook.timezone || 'UTC';
  const failed = playbook.last_generation_status === 'failed';

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-secondary/10 p-3 space-y-2" data-testid={`playbook-schedule-panel-${playbook.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Automatic draft creation</p>
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`generation-schedule-disclaimer-${playbook.id}`}>{GENERATION_SCHEDULE_DISCLAIMER}</p>
          <p className="text-[11px] text-muted-foreground">This controls when AI drafts are created. Review and LinkedIn timing stay on Draft Posts.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {!configured ? (
            <button
              type="button"
              onClick={() => onSchedule(playbook)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs"
              data-testid={`schedule-generation-btn-${playbook.id}`}
            >
              <Clock className="w-3.5 h-3.5" /> Schedule AI drafts
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onSchedule(playbook)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-xs"
                data-testid={`edit-generation-schedule-btn-${playbook.id}`}
              >
                Edit AI draft schedule
              </button>
              <button
                type="button"
                onClick={() => onCancelSchedule(playbook)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-xs text-muted-foreground"
                data-testid={`cancel-generation-schedule-btn-${playbook.id}`}
              >
                Cancel AI draft schedule
              </button>
            </>
          )}
        </div>
      </div>

      {configured && (
        <div className="grid gap-1 text-[11px] text-muted-foreground font-mono" data-testid={`schedule-status-${playbook.id}`}>
          <p>
            Recurring: {playbook.generation_schedule_enabled ? 'enabled' : 'disabled'} · {playbook.generation_schedule_summary || playbook.generation_frequency}
          </p>
          <p data-testid={`next-generation-${playbook.id}`}>
            Next AI generation: {playbook.generation_schedule_enabled && playbook.next_generation_at
              ? formatPlaybookLocal(playbook.next_generation_at.endsWith?.('Z') ? playbook.next_generation_at : `${playbook.next_generation_at}Z`, tz)
              : (playbook.paused ? 'Paused (override)' : '—')}
          </p>
          <p data-testid={`last-generation-${playbook.id}`}>
            Last generation: {playbook.last_generation_status || '—'}
            {playbook.last_generation_at
              ? ` · ${formatPlaybookLocal(String(playbook.last_generation_at).endsWith('Z') ? playbook.last_generation_at : `${playbook.last_generation_at}Z`, tz)}`
              : ''}
          </p>
          {failed && (
            <p className="text-status-down" data-testid={`generation-failure-${playbook.id}`}>
              Failed: {playbook.last_generation_error || 'Unknown error'}
              {playbook.generation_next_retry_at
                ? ` · Next retry ${formatPlaybookLocal(String(playbook.generation_next_retry_at).endsWith('Z') ? playbook.generation_next_retry_at : `${playbook.generation_next_retry_at}Z`, tz)}`
                : ''}
            </p>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-white/5 space-y-1" data-testid={`publishing-controls-${playbook.id}`}>
        <p className="text-xs font-semibold text-foreground uppercase tracking-wide">LinkedIn publishing</p>
        <p className="text-[11px] text-muted-foreground">Mode: {modeLabel(playbook.mode)} · Cap {playbook.publishes_today ?? 0}/{playbook.max_publishes_per_day ?? 1} today</p>
        <p className="text-[11px] text-muted-foreground">{PUBLISHING_MODE_HINT}</p>
        {playbook.mode === 'guarded_autopilot' && (
          <p className="text-[11px] text-muted-foreground" data-testid={`autopilot-window-summary-${playbook.id}`}>
            Autopilot publish: {resolveAutopilotPublishSummary(playbook)} ({tz})
          </p>
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, apps, onDeleted, onRan }) {
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [running, setRunning] = useState(false);
  const app = apps.find((a) => a.id === task.app_id);
  const supported = ACTIONS.includes(task.action_name);
  const fetchRuns = async () => {
    if (expanded) { setExpanded(false); return; }
    setLoadingRuns(true);
    setExpanded(true);
    try {
      const res = await api.get(`/tasks/${task.id}/runs?limit=10`);
      setRuns(asArray(res.data));
    } catch {
      toast.error('Failed to load run history');
    } finally {
      setLoadingRuns(false);
    }
  };
  const handleRun = async () => {
    if (!supported) return;
    setRunning(true);
    try {
      const res = await api.post(`/tasks/${task.id}/run`);
      toast.success(`Task ${res.data.status}`);
      onRan();
    } catch {
      toast.error('Task run failed');
    } finally {
      setRunning(false);
    }
  };
  const handleDelete = async () => {
    if (!window.confirm(`Delete task "${task.name}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Task deleted');
      onDeleted(task.id);
    } catch {
      toast.error('Failed to delete task');
    }
  };
  return (
    <div className="bg-card border border-white/5 rounded-xl overflow-hidden" data-testid={`task-row-${task.id}`}>
      <div className="flex items-center gap-4 p-4">
        <div className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-primary" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-foreground truncate">{task.name}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{task.frequency}</span>
            {!supported && <span className="text-xs px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-200" data-testid={`task-unsupported-${task.id}`}>UNSUPPORTED / INACTIVE</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{task.action_name}{app ? ` · ${app.name}` : ''}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={handleRun} disabled={running || !supported} className="p-2 rounded-lg hover:bg-primary/10 text-primary disabled:opacity-40" data-testid={`run-task-btn-${task.id}`}>
            {running ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={fetchRuns} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
          <button onClick={handleDelete} className="p-2 rounded-lg hover:bg-status-down/10 text-status-down"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-white/5 px-4 py-3 bg-secondary/10">
          {loadingRuns ? <div className="text-xs text-muted-foreground">Loading...</div> : runs.length === 0 ? <p className="text-xs text-muted-foreground">No runs yet</p> : (
            <div className="space-y-2">
              {runs.map((run) => (
                <div key={run.id} className="flex items-start gap-2 text-xs">
                  {run.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-status-up mt-0.5" /> : <XCircle className="w-3.5 h-3.5 text-status-down mt-0.5" />}
                  <p className="text-foreground/80">{run.result_message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Automations() {
  const [tasks, setTasks] = useState([]);
  const [apps, setApps] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [pausePublishing, setPausePublishing] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [playbookModal, setPlaybookModal] = useState(null); // null | 'create' | playbook
  const [runOncePb, setRunOncePb] = useState(null);
  const [schedulePb, setSchedulePb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [runningPb, setRunningPb] = useState(null);
  const [inspectingPb, setInspectingPb] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = async () => {
    try {
      setLoadError(null);
      const [tasksRes, appsRes, pbRes, inboxRes, pauseRes, identitiesRes] = await Promise.all([
        api.get('/tasks/'),
        api.get('/apps/'),
        api.get('/campaign-automations/playbooks'),
        api.get('/campaign-automations/approval-inbox'),
        api.get('/campaign-automations/publishing-pause'),
        api.get('/linkedin/identities').catch(() => ({ data: [] })),
      ]);
      setTasks(asArray(tasksRes.data));
      setApps(asArray(appsRes.data));
      setPlaybooks(asArray(pbRes.data));
      setInbox(asArray(inboxRes.data));
      setIdentities(asArray(identitiesRes.data));
      setPausePublishing(!!pauseRes.data?.pause_publishing);
    } catch {
      setLoadError('Failed to load automations');
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (searchParams.get('add') === 'true') {
      setShowCreateTask(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const togglePauseAll = async () => {
    try {
      const res = await api.put('/campaign-automations/publishing-pause', { pause_publishing: !pausePublishing });
      setPausePublishing(!!res.data.pause_publishing);
      toast.success(res.data.pause_publishing ? 'All AI generation and publishing paused' : 'Automation resumed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update publishing pause');
    }
  };

  const confirmRunOnce = async (topic) => {
    const id = runOncePb.id;
    setRunOncePb(null);
    setRunningPb(id);
    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `run-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      const res = await api.post(`/campaign-automations/playbooks/${id}/run-once`, {
        idempotency_key: idempotencyKey,
        topic,
      });
      toast.success(`Generation ${res.data.status}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Run failed');
    } finally {
      setRunningPb(null);
    }
  };

  const pausePlaybook = async (id, paused) => {
    try {
      await api.post(`/campaign-automations/playbooks/${id}/${paused ? 'resume' : 'pause'}`);
      await fetchData();
    } catch {
      toast.error('Failed to update playbook');
    }
  };

  const inspectSource = async (pb) => {
    setInspectingPb(pb.id);
    try {
      const res = await api.post(`/campaign-automations/playbooks/${pb.id}/inspect-source`);
      toast.success(`Inspected ${res.data?.evidence?.length || 0} approved source page(s) — no AI credits used`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Source inspection failed');
    } finally {
      setInspectingPb(null);
    }
  };

  const archivePlaybook = async (pb) => {
    if (!window.confirm(`Archive playbook "${pb.name}"? History is preserved.`)) return;
    try {
      await api.post(`/campaign-automations/playbooks/${pb.id}/archive`);
      toast.success('Playbook archived');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  const cancelSchedule = async (pb) => {
    if (!window.confirm(`Cancel recurring AI generation schedule for "${pb.name}"? Draft Posts and LinkedIn publish queues are unchanged.`)) return;
    try {
      const res = await api.delete(`/campaign-automations/playbooks/${pb.id}/generation-schedule`);
      setPlaybooks(playbooks.map((p) => (p.id === pb.id ? res.data : p)));
      toast.success('Generation schedule cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  const rejectCandidate = async (post) => {
    const reason = window.prompt('Rejection reason (required):');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/campaign-automations/candidates/${post.id}/reject`, { reason: reason.trim() });
      toast.success('Candidate rejected');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  const approveCandidate = async (post) => {
    const failed = (post.quality_gate_results?.checks || []).some((c) => !c.ok);
    if (failed || post.quality_gate_results?.passed === false) {
      toast.error('Deterministic checks failed — approve disabled');
      return;
    }
    try {
      await api.post(`/posts/${post.id}/approve`);
      toast.success('Candidate approved — schedule or Publish Now from Posts');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="automations-loading"><Loader className="w-6 h-6 text-primary animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="automations-page">
      {loadError && <div className="text-sm text-status-down" data-testid="automations-error">{loadError}</div>}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Automations</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose what to promote, let AI prepare drafts, review the quality, then publish when you are ready.</p>
        </div>
        <button onClick={togglePauseAll} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${pausePublishing ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-secondary/40 border-white/10 text-foreground'}`} data-testid="pause-all-publishing-btn">
          <ShieldAlert className="w-4 h-4" />
          {pausePublishing ? 'All automation paused — Resume' : 'Pause all automation'}
        </button>
      </div>

      <WorkflowOverview
        playbooks={playbooks}
        inbox={inbox}
        pausePublishing={pausePublishing}
        showHelp={showHelp}
        onToggleHelp={() => setShowHelp(!showHelp)}
      />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Your campaigns</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Each campaign remembers the product, writing rules, approval flow, and LinkedIn destination.</p>
          </div>
          <button onClick={() => setPlaybookModal('create')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-medium" data-testid="new-playbook-btn">
            <Plus className="w-3.5 h-3.5" /> Create campaign
          </button>
        </div>
        {playbooks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-8 text-center" data-testid="empty-playbooks">
            <Sparkles className="w-7 h-7 text-primary mx-auto" />
            <p className="text-sm font-medium text-foreground mt-3">Create your first campaign</p>
            <p className="text-xs text-muted-foreground mt-1">Connect a product website, define what AI may say, and choose how drafts should be approved.</p>
            <button onClick={() => setPlaybookModal('create')} className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-xs font-medium"><Plus className="w-3.5 h-3.5" /> Create campaign</button>
          </div>
        ) : (
          <div className="space-y-3">
            {playbooks.map((pb) => (
              <div key={pb.id} className="bg-card border border-white/5 rounded-xl p-4" data-testid={`playbook-row-${pb.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-medium text-foreground">{pb.name}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{modeLabel(pb.mode)}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${pb.paused ? 'text-amber-200 border-amber-500/30' : 'text-status-up border-status-up/20'}`}>{pb.paused ? 'PAUSED' : 'ACTIVE'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Awaiting approval: {pb.awaiting_approval_count} · Approved-or-later: {pb.approved_post_count}
                      {' · '}Gens today: {pb.generations_today}/{pb.max_generations_per_day}
                      {' · '}Publishes today: {pb.publishes_today ?? 0}/{pb.max_publishes_per_day ?? 1}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`playbook-identity-${pb.id}`}>LinkedIn identity: {identityLabel(identities, pb.publishing_identity_id)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`playbook-timezone-${pb.id}`}>Timezone: {pb.timezone || 'UTC'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`playbook-source-${pb.id}`}>
                      Content source: {pb.source_app_name || (pb.source_app_id ? 'Attached App Registry website' : 'Manual playbook')}
                      {pb.source_app_id && ` · ${pb.last_source_status || 'not inspected'}`}
                    </p>
                    {pb.source_app_id && pb.source_search_checks_total > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`playbook-search-readiness-${pb.id}`}>
                        Google Search readiness: {pb.source_search_checks_passed}/{pb.source_search_checks_total} checks passed across {pb.source_page_count} page(s)
                      </p>
                    )}
                    {pb.last_source_error && <p className="text-[11px] text-status-down mt-1" data-testid={`playbook-source-error-${pb.id}`}>{pb.last_source_error}</p>}
                    {pb.paused && (
                      <p className="text-[11px] text-amber-200/90 mt-1" data-testid={`pause-override-${pb.id}`}>
                        Pause overrides both AI generation and LinkedIn publishing for this campaign.
                      </p>
                    )}
                    {(() => {
                      const next = nextPlaybookAction(pb);
                      const toneClass = next.tone === 'amber'
                        ? 'border-amber-500/20 bg-amber-500/5 text-amber-100'
                        : next.tone === 'green'
                          ? 'border-status-up/20 bg-status-up/5 text-status-up'
                          : 'border-primary/20 bg-primary/5 text-primary';
                      return (
                        <div className={`mt-2 rounded-lg border px-3 py-2 ${toneClass}`} data-testid={`playbook-next-step-${pb.id}`}>
                          <p className="text-xs font-medium">{next.title}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{next.detail}</p>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {pb.source_app_id && (
                      <button onClick={() => inspectSource(pb)} disabled={inspectingPb === pb.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary/20 text-primary text-xs disabled:opacity-50" data-testid={`inspect-source-btn-${pb.id}`}>
                        {inspectingPb === pb.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Inspect website
                      </button>
                    )}
                    <button onClick={() => setRunOncePb(pb)} disabled={!!runningPb || pb.paused} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs disabled:opacity-50" data-testid={`run-playbook-btn-${pb.id}`}>
                      {runningPb === pb.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Create draft now
                    </button>
                    <button onClick={() => setPlaybookModal(pb)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-xs" data-testid={`edit-playbook-btn-${pb.id}`}><Pencil className="w-3.5 h-3.5" /> Edit setup</button>
                    <button onClick={() => pausePlaybook(pb.id, pb.paused)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-xs text-muted-foreground" data-testid={`pause-playbook-btn-${pb.id}`}>
                      <Pause className="w-3.5 h-3.5" />{pb.paused ? 'Resume' : 'Pause'}
                    </button>
                    <button onClick={() => archivePlaybook(pb)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-xs text-muted-foreground" data-testid={`archive-playbook-btn-${pb.id}`}><Archive className="w-3.5 h-3.5" /> Archive</button>
                  </div>
                </div>
                <PlaybookSchedulePanel
                  playbook={pb}
                  onSchedule={setSchedulePb}
                  onCancelSchedule={cancelSchedule}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Drafts waiting for you</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Review the draft and its checks. Approval makes it eligible for Publish Now or Schedule post on Draft Posts.</p>
        </div>
        {inbox.length === 0 ? (
          <div className="rounded-xl border border-white/5 p-6 text-sm text-muted-foreground" data-testid="empty-approval-inbox">Nothing needs your approval right now.</div>
        ) : (
          <div className="space-y-3">
            {inbox.map((post) => {
              const checks = post.quality_gate_results?.checks || [];
              const gatesFailed = post.quality_gate_results?.passed === false || checks.some((c) => !c.ok);
              return (
                <div key={post.id} className="bg-card border border-white/5 rounded-xl p-4" data-testid={`inbox-row-${post.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground">{post.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-3 whitespace-pre-wrap">{post.content}</p>
                      <p className="text-[11px] text-muted-foreground mt-2 font-mono">Score: {post.quality_score ?? '—'} · Gates: {post.quality_gate_results?.passed ? 'pass' : 'fail/pending'}</p>
                      {Array.isArray(post.source_evidence) && post.source_evidence.length > 0 && (
                        <div className="mt-2 rounded-lg border border-primary/15 bg-primary/5 p-2" data-testid={`source-evidence-${post.id}`}>
                          <p className="text-[11px] font-medium text-foreground flex items-center gap-1"><Globe2 className="w-3 h-3" /> Why this draft was created</p>
                          <div className="mt-1 space-y-1">
                            {post.source_evidence.map((source) => (
                              <div key={source.url} className="text-[11px] text-muted-foreground">
                                <a href={source.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{source.title || source.url}</a>
                                {Array.isArray(source.search_checks) && (
                                  <span> · Search readiness {source.search_checks.filter((check) => check.ok).length}/{source.search_checks.length}</span>
                                )}
                                {Array.isArray(source.search_checks) && source.search_checks.some((check) => !check.ok) && (
                                  <ul className="ml-3 mt-0.5 text-amber-200/80">
                                    {source.search_checks.filter((check) => !check.ok).map((check) => (
                                      <li key={check.name}>Needs attention: {check.name} — {check.detail}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {Array.isArray(checks) && (
                        <ul className="mt-2 space-y-0.5 text-[11px]" data-testid={`gate-checks-${post.id}`}>
                          {checks.map((c) => (
                            <li key={c.name} className={c.ok ? 'text-status-up/80' : 'text-status-down'}>{c.ok ? '✓' : '✗'} {c.name}: {c.detail}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button onClick={() => approveCandidate(post)} disabled={gatesFailed} className="px-3 py-1.5 rounded-lg bg-status-up/15 text-status-up text-xs disabled:opacity-40" data-testid={`approve-candidate-${post.id}`}>Approve</button>
                      <a href="/posts" className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-center" data-testid={`open-draft-${post.id}`}>Open / Edit Draft</a>
                      <button onClick={() => rejectCandidate(post)} className="px-3 py-1.5 rounded-lg bg-status-down/10 text-status-down text-xs" data-testid={`reject-candidate-${post.id}`}>Reject</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-xl border border-white/5 bg-card/30 p-4" data-testid="app-checks-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Settings2 className="w-4 h-4 text-muted-foreground" /> Advanced: App health checks</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Optional technical monitoring. This is separate from campaigns and social posts. {tasks.length} task{tasks.length === 1 ? '' : 's'} configured.</p>
          </div>
          <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-xs" data-testid="advanced-tasks-toggle">
            {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {showAdvanced ? 'Hide' : 'Show'}
          </button>
        </div>
        {showAdvanced && (
          <div className="space-y-3" data-testid="advanced-tasks-content">
            <div className="flex justify-end">
              <button onClick={() => setShowCreateTask(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary/50 text-foreground text-xs font-medium" data-testid="new-task-btn"><Plus className="w-3.5 h-3.5" /> New health check</button>
            </div>
            {tasks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground" data-testid="empty-tasks">No health-check tasks yet.</div>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => (
                  <TaskRow key={task.id} task={task} apps={apps} onDeleted={(id) => setTasks(tasks.filter((t) => t.id !== id))} onRan={fetchData} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {showCreateTask && <CreateTaskModal apps={apps} onClose={() => setShowCreateTask(false)} onCreated={(task) => setTasks([task, ...tasks])} />}
      {playbookModal && (
        <PlaybookFormModal
          identities={identities}
          apps={apps}
          initial={playbookModal === 'create' ? null : playbookModal}
          onClose={() => setPlaybookModal(null)}
          onSaved={(pb, editing) => {
            if (editing) setPlaybooks(playbooks.map((p) => (p.id === pb.id ? pb : p)));
            else setPlaybooks([pb, ...playbooks]);
          }}
        />
      )}
      {runOncePb && <RunOnceDialog playbook={runOncePb} onClose={() => setRunOncePb(null)} onConfirm={confirmRunOnce} />}
      {schedulePb && (
        <ScheduleGenerationModal
          playbook={schedulePb}
          onClose={() => setSchedulePb(null)}
          onSaved={(pb) => setPlaybooks(playbooks.map((p) => (p.id === pb.id ? pb : p)))}
        />
      )}
    </div>
  );
}
